import { createApi } from '@reduxjs/toolkit/query/react';
import {
  AgentMapDto,
  AgentMapSeatDto,
  ChangeAgentStatusAgentMapSeatDto,
  CreateAgentMapDto,
  FindAllAgentMapDto,
  ResponseDto,
  ResponsePagingDto,
  UpdateAgentMapDto
} from 'types';
import axiosBaseQuery from 'utils/base-api';

export const agentMapsApi = createApi({
  reducerPath: 'agentMapsApi',
  tagTypes: ['agent_maps', 'agent_maps_detail', 'AgentSeat'], // Added 'AgentSeat'
  baseQuery: axiosBaseQuery,
  endpoints: (builder) => ({
    getAgentMaps: builder.query<ResponsePagingDto<AgentMapDto>, FindAllAgentMapDto>({
      query: (params) => ({
        url: '/agent_maps',
        method: 'get',
        params
      }),
      providesTags: (result) =>
        result && result.data.rows.length > 0
          ? result.data.rows.map(({ agentMapId }) => ({
            type: 'agent_maps',
            id: agentMapId
          }))
          : ['agent_maps']
    }),

    getAgentMapDetail: builder.query<ResponseDto<AgentMapDto>, number>({
      query: (agentMapId) => ({
        url: `/agent_maps/${agentMapId}`,
        method: 'get'
      }),
      providesTags: (result) => (result?.data ? [{ type: 'agent_maps', id: result.data.agentMapId }] : [])
    }),

    changeAgentStatusAgentMapSeat: builder.mutation<ResponseDto<any>, ChangeAgentStatusAgentMapSeatDto>({
      query: ({ userId, ...status }) => ({
        url: `/agent_maps/seat/user/${userId}`,
        method: 'patch',
        data: status
      }),
    }),

    createAgentMap: builder.mutation<ResponseDto<AgentMapDto>, CreateAgentMapDto>({
      query: (newSchedule) => ({
        url: '/agent_maps',
        method: 'post',
        data: newSchedule
      }),
      invalidatesTags: ['agent_maps']
    }),
    updateAgentMap: builder.mutation<ResponseDto<AgentMapDto>, UpdateAgentMapDto>({
      query: ({ agentMapId, ...updateSchedule }) => ({
        url: `/agent_maps/${agentMapId}`,
        method: 'patch',
        data: updateSchedule
      }),
      invalidatesTags: (_result, _error, param) => [
        { type: 'agent_maps', id: param.agentMapId },
        { type: 'agent_maps_detail', id: param.agentMapId }
      ]
    }),
    deleteAgentMap: builder.mutation<ResponseDto<null>, number>({
      query: (agentMapId) => ({
        url: `/agent_maps/${agentMapId}`,
        method: 'delete'
      }),
      invalidatesTags: (_result, _error, param) => [
        { type: 'agent_maps', id: param },
        { type: 'agent_maps_detail', id: param }
      ]
    }),
    getAgentMapSeatByUserId: builder.query<ResponseDto<AgentMapSeatDto>, number>({
      query: (userId) => ({
        url: `/agent_maps/seat/user/${userId}`,
        method: 'get',
      }),
    }),
    getOnlineAgentMapSeats: builder.query<ResponseDto<AgentMapSeatDto[]>, void>({
      query: () => ({
        url: '/agent_maps/seat/user/online',
        method: 'get',
      }),
      // Assuming AgentMapSeatDto contains a 'userId' property.
      // This tags each seat status by its userId and the list as a whole.
      providesTags: (result) =>
        result && result.data
          ? [
            ...result.data.map(({ userId }) => ({ type: 'AgentSeat' as const, id: userId })), // Tag each agent's seat status
            { type: 'agent_maps' as const, id: 'ONLINE_LIST' }, // Tag for the entire list of online seats
          ]
          : [{ type: 'agent_maps' as const, id: 'ONLINE_LIST' }],
    }),

  })
});

export const {
  useGetAgentMapsQuery,
  useGetAgentMapDetailQuery,
  useLazyGetAgentMapDetailQuery,
  useCreateAgentMapMutation,
  useUpdateAgentMapMutation,
  useDeleteAgentMapMutation,
  useChangeAgentStatusAgentMapSeatMutation,
  useLazyGetAgentMapSeatByUserIdQuery,
  useGetOnlineAgentMapSeatsQuery, // Added new hook
  useLazyGetOnlineAgentMapSeatsQuery, // Added new lazy hook
} = agentMapsApi;
